import * as React from 'react'
import { ReactElement } from 'react'
import { TreeItemLayout } from '@fluentui/react-components/unstable'
import { DataBarVerticalAscendingRegular, GiftCardRegular, PaymentRegular, TimerRegular, VehicleCarRegular } from '@fluentui/react-icons'
import { InvoiceListItemDto, InvoiceMutationDto, LineItemMutationDto, LineItemType } from '../../../../../config/finance/invoiceConfig'
import { DisbursementListItemDto, FixedPriceListItemDto, TimeListItemDto, TravelListItemDto } from '../../../../../config/registrationConfig'
import TimeLineItem from './TimeLineItem'
import DisbursementLineItem from './DisbursementLineItem'
import { css, getId, Stack } from '@fluentui/react'
import { makeStyles } from '@fluentui/react-components'
import TravelLineItem from './TravelLineItem'
import { useFormikContext } from 'formik'
import MoneyText from '../../../../Text/Currency/MoneyText'

import { LineItemEntities, LineItemEntity } from '../../../../../hooks/finance/invoice/useLineItemEntities'
import InvoiceLineItem from './InvoiceLineItem'
import { TreeItemLayoutProps } from '@fluentui/react-tree'
import { SurchargeListItemDto } from '../../../../../config/finance/financeConfig'
import SurchargeLineItem from './SurchargeLineItem'
import useVatTypes from '../../../../../hooks/useVatTypes'
import FixedPriceLineItem from './FixedPriceLineItem'
import { useTranslation } from 'react-i18next'

export type LineItemProps<E extends LineItemEntity> = {
    lineItemType: LineItemType
    lineItem?: LineItemMutationDto
    entity?: E
    defaultVatType: string
    showPrice: boolean
    findQuantity: LineItemEntities['findQuantity']
    getPrice: LineItemEntities['getPrice']
    hasMultipleFiles: boolean
    isSelected?: boolean
}

type Props<E extends LineItemEntity> = LineItemProps<E> & {
    isDraggable: boolean
    actions?: TreeItemLayoutProps['actions']
}

export const LineItemTypeIcons: Map<LineItemType, ReactElement> = new Map<LineItemType, ReactElement>([
    [LineItemType.Disbursement, <PaymentRegular key={getId()} />],
    [LineItemType.FixedPrice, <GiftCardRegular key={getId()} />],
    [LineItemType.Invoice, <PaymentRegular key={getId()} />],
    [LineItemType.Surcharge, <DataBarVerticalAscendingRegular key={getId()} />],
    [LineItemType.Time, <TimerRegular key={getId()} />],
    [LineItemType.Travel, <VehicleCarRegular key={getId()} />],
])

const useStyles = makeStyles({
    root: {
        // Prevent accidentally selecting inside the line item.
        userSelect: 'none',
    },
})

export default function LineItem(props: Props<any>): ReactElement {
    const { t: translate } = useTranslation()
    const styles = useStyles()
    const { values } = useFormikContext<InvoiceMutationDto>()
    const { getVatType } = useVatTypes()

    return (
        <TreeItemLayout
            iconBefore={<Stack styles={{ root: { cursor: props.isDraggable && 'move' } }}>{LineItemTypeIcons.get(props.lineItemType)}</Stack>}
            className={css(styles.root)}
            aside={
                props.showPrice && (
                    <MoneyText
                        amount={props.getPrice(props.entity ?? props.lineItem, getVatType(props.defaultVatType)).subtotal}
                        alpha={values.currency}
                    />
                )
            }
            actions={props.actions || undefined}
        >
            {(() => {
                if (!props.entity && !props.lineItem) {
                    return <>{translate(`invoice.edit.form.invoiceline.deleted.${props.lineItemType.toLowerCase()}`)}</>
                }

                switch (props.lineItemType) {
                    case LineItemType.Disbursement:
                        return <DisbursementLineItem {...(props as LineItemProps<DisbursementListItemDto>)} />
                    case LineItemType.FixedPrice:
                        return <FixedPriceLineItem {...(props as LineItemProps<FixedPriceListItemDto>)} />
                    case LineItemType.Invoice:
                        return <InvoiceLineItem {...(props as LineItemProps<InvoiceListItemDto>)} />
                    case LineItemType.Surcharge:
                        return <SurchargeLineItem {...(props as LineItemProps<SurchargeListItemDto>)} />
                    case LineItemType.Time:
                        return <TimeLineItem {...(props as LineItemProps<TimeListItemDto>)} />
                    case LineItemType.Travel:
                        return <TravelLineItem {...(props as LineItemProps<TravelListItemDto>)} />
                    default:
                        return <></>
                }
            })()}
        </TreeItemLayout>
    )
}
